import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, Filter, Search } from 'lucide-react';
import {
  BosDurum,
  Girdi,
  Rozet,
  TarihAraligiSecici,
  VeriTablo,
  tarihIso,
  type ColumnDef,
  type RozetTonu,
  type TarihAraligi,
} from '@emanetx/ui';
import {
  odemeleriListele,
  type Odeme,
  type OdemeDurumu,
  type OdemeSaglayicisi,
} from '@emanetx/sdk';
import { istemci } from '../istemci.js';
import { telefonMaskele } from '../kiralamalar/durumlar.js';

const DURUM_TONU: Record<OdemeDurumu, RozetTonu> = {
  BEKLEMEDE: 'turuncu',
  BASARILI: 'yesil',
  BASARISIZ: 'kirmizi',
  IADE_EDILDI: 'mavi',
};

const DURUM_ETIKET: Record<OdemeDurumu, string> = {
  BEKLEMEDE: 'Beklemede',
  BASARILI: 'Başarılı',
  BASARISIZ: 'Başarısız',
  IADE_EDILDI: 'İade',
};

function sonOtuzGun(): TarihAraligi {
  const e = new Date();
  const s = new Date();
  s.setDate(s.getDate() - 29);
  s.setHours(0, 0, 0, 0);
  return { baslangic: s, bitis: e };
}

export function SayfaOdemeler() {
  const [sayfa, setSayfa] = useState(1);
  const [arama, setArama] = useState('');
  const [durum, setDurum] = useState<OdemeDurumu | ''>('');
  const [saglayici, setSaglayici] = useState<OdemeSaglayicisi | ''>('');
  const [araliki, setAralik] = useState<TarihAraligi>(sonOtuzGun);

  const sorgu = {
    sayfa,
    boyut: 50,
    ...(arama && { arama }),
    ...(durum && { durum: durum as OdemeDurumu }),
    ...(saglayici && { saglayici: saglayici as OdemeSaglayicisi }),
    ...(tarihIso(araliki.baslangic) && { baslangicTen: tarihIso(araliki.baslangic) }),
    ...(tarihIso(araliki.bitis) && { baslangicAKadar: tarihIso(araliki.bitis) }),
  };

  const liste = useQuery({
    queryKey: ['odemeler', 'liste', sorgu],
    queryFn: () => odemeleriListele(istemci, sorgu),
    placeholderData: (onceki) => onceki,
  });

  const kolonlar = useMemo<ColumnDef<Odeme, unknown>[]>(
    () => [
      {
        accessorKey: 'olusturmaTarihi',
        header: 'Zaman',
        meta: { etiket: 'Zaman' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.olusturmaTarihi).toLocaleString('tr-TR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ),
      },
      {
        accessorKey: 'kiralamaKod',
        header: 'Kiralama',
        meta: { etiket: 'Kiralama' },
        cell: ({ row }) => (
          <span className="text-marka-700 dark:text-marka-400 font-mono text-xs">
            {row.original.kiralamaKod ?? '—'}
          </span>
        ),
      },
      {
        id: 'musteri',
        header: 'Müşteri',
        meta: { etiket: 'Müşteri' },
        cell: ({ row }) => (
          <div>
            <div className="text-fume-700 dark:text-fume-300 font-mono text-xs">
              {row.original.musteriTelefon ? telefonMaskele(row.original.musteriTelefon) : '—'}
            </div>
            {row.original.musteriAdSoyad && (
              <div className="text-fume-400 text-xs">{row.original.musteriAdSoyad}</div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'miktar',
        header: 'Tutar',
        meta: { etiket: 'Tutar' },
        cell: ({ row }) => {
          const sembol =
            row.original.paraBirimi === 'TRY' ? '₺' : row.original.paraBirimi === 'EUR' ? '€' : '$';
          return (
            <span className="font-mono font-semibold">
              {sembol}
              {row.original.miktar}
            </span>
          );
        },
        sortingFn: (a, b) => Number(a.original.miktar) - Number(b.original.miktar),
      },
      {
        accessorKey: 'saglayici',
        header: 'Sağlayıcı',
        meta: { etiket: 'Sağlayıcı' },
        cell: ({ row }) => <Rozet ton="fume">{row.original.saglayici}</Rozet>,
      },
      {
        accessorKey: 'durum',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) => (
          <Rozet ton={DURUM_TONU[row.original.durum]} nokta>
            {DURUM_ETIKET[row.original.durum]}
          </Rozet>
        ),
      },
      {
        accessorKey: 'saglayiciRef',
        header: 'Referans',
        meta: { etiket: 'Referans' },
        cell: ({ row }) => (
          <span className="text-fume-400 font-mono text-xs">
            {row.original.saglayiciRef ?? '—'}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Ödemeler</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Tüm kiralama ödemeleri — sağlayıcı, tutar, durum.
        </p>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        varsayilanGizliKolonlar={{ saglayiciRef: false }}
        bosDurum={
          <BosDurum
            ikon={CreditCard}
            baslik="Bu tarih aralığında ödeme yok"
            aciklama="Tarih aralığını veya filtreyi değiştirin."
          />
        }
        ustAksiyonlar={
          <>
            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <Search
                size={14}
                className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="Kiralama kodu, telefon, referans..."
                value={arama}
                onChange={(e) => {
                  setSayfa(1);
                  setArama(e.target.value);
                }}
                className="h-10 pl-9 text-sm"
              />
            </div>
            <TarihAraligiSecici
              deger={araliki}
              onDegisim={(a) => {
                setAralik(a);
                setSayfa(1);
              }}
              etiketYok
              className="h-10 text-xs"
            />
            <div className="flex items-center gap-2">
              <Filter size={13} className="text-fume-400" />
              <select
                value={saglayici}
                onChange={(e) => {
                  setSaglayici(e.target.value as OdemeSaglayicisi | '');
                  setSayfa(1);
                }}
                className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
              >
                <option value="">Tüm sağlayıcılar</option>
                <option value="IYZICO">iyzico</option>
                <option value="PARAM">Param</option>
                <option value="AKBANK">Akbank</option>
                <option value="CUZDAN">Cüzdan</option>
                <option value="MANUEL">Manuel</option>
              </select>
              <select
                value={durum}
                onChange={(e) => {
                  setDurum(e.target.value as OdemeDurumu | '');
                  setSayfa(1);
                }}
                className="border-fume-200 focus:ring-marka-500 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs focus:outline-none focus:ring-2"
              >
                <option value="">Tüm durumlar</option>
                <option value="BEKLEMEDE">Beklemede</option>
                <option value="BASARILI">Başarılı</option>
                <option value="BASARISIZ">Başarısız</option>
                <option value="IADE_EDILDI">İade</option>
              </select>
            </div>
          </>
        }
      />
    </div>
  );
}
